import {
  Divider, Page, Spinner
} from "@whitespace/whitespace-ui";
import { useNavigate, Link } from "react-router-dom";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import AmplifyError from "components/AmplifyError/AmplifyError";
import { format } from "date-fns";
import {
  Button,
  Container, Card, Col, Row,
  Form,
  Breadcrumb
} from "react-bootstrap";
import { RoutePathsEnum } from "infrastructure/RoutePathsEnum";
import { convertHoursAndMinutesToDecimal, convertUtcDateToLocalDate } from "helpers/DateUtils";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import IGetSlaDetailsResponseModel from "interfaces/Response/IGetSlaDetailsResponseModel";
import ISlaDetailsViewModel from "interfaces/ISlaDetailsViewModel";
import { TimePickerComponent } from "@syncfusion/ej2-react-calendars";
import IUpdateDriverWorkingHoursRequestModel from "interfaces/Request/IUpdateDriverWorkingHoursRequestModel";
import { mapSlaDetailsToViewModel } from "./DataMappers/SlaDetailsMapper";

import "./EditDriverWorkingHours.scss";

const EditDriverWorkingHours = () => {
  const {
    isSending: getSlaDetailsIsSending, errors: getSlaDetailsErrors, data: getSlaDetailsData, sendRequest: getSlaDetailsSendRequest
  } = useAmplifyREST();
  const {
    isSending: updateDriveWorkingHoursIsSending, errors: updateDriveWorkingHoursErrors, sendRequest: updateDriveWorkingHoursSendRequest,
    statusCode: updateDriveWorkingHoursStatusCode
  } = useAmplifyREST();

  const isSending = getSlaDetailsIsSending || updateDriveWorkingHoursIsSending;
  const apiErrors = getSlaDetailsErrors || updateDriveWorkingHoursErrors;
  const [slaDetailsViewModel, setSlaDetailsViewModel] = useState<ISlaDetailsViewModel | undefined>();
  const navigate = useNavigate();
  const [showExceptions, setShowExceptions] = useState<boolean>(false);
  const [dateHasChanged, setDateHasChanged] = useState<boolean>(false);
  const [canFormBeSubmitted, setCanFormBeSubmitted] = useState<boolean>(false);
  const [startTimeInput, setStartTimeInput] = useState<Date | undefined>();
  const [endTimeInput, setEndTimeInput] = useState<Date>();
  const [isStartTimeAfterEndTimeError, setIsStartTimeAfterEndTimeError] = useState<any>();

  const {
    handleSubmit
  } = useForm();

  let startTimeRef : TimePickerComponent | undefined;
  let endTimeRef : TimePickerComponent | undefined;

  useEffect(() => {
    getSlaDetailsSendRequest("settings/sla/details", "GET");
  }, [getSlaDetailsSendRequest]);

  useEffect(() => {
    if (getSlaDetailsData) {
      const response : IGetSlaDetailsResponseModel = getSlaDetailsData;
      setSlaDetailsViewModel(mapSlaDetailsToViewModel(response.slaDetails));
      setStartTimeInput(new Date(response.slaDetails.dwhStartTime));
      setEndTimeInput(new Date(response.slaDetails.dwhEndTime));
    }
  }, [getSlaDetailsData]);

  const canFormBeSubmittedCheck = () => {
    if (slaDetailsViewModel) {
      setIsStartTimeAfterEndTimeError(false);
      if (!startTimeRef || !startTimeRef.value || !endTimeRef || !endTimeRef.value) {
        setCanFormBeSubmitted(false);
        return;
      }

      const startTimeInputCompare = convertHoursAndMinutesToDecimal(startTimeRef.value.getHours(), startTimeRef.value.getMinutes());
      const startTimeCurrentCompare = convertHoursAndMinutesToDecimal(
        slaDetailsViewModel.dwhStartTime.getHours(),
        slaDetailsViewModel.dwhStartTime.getMinutes()
      );
      const endTimeInputCompare = convertHoursAndMinutesToDecimal(endTimeRef.value.getHours(), endTimeRef.value.getMinutes());
      const endTimeCurrentCompare = convertHoursAndMinutesToDecimal(
        slaDetailsViewModel.dwhEndTime.getHours(),
        slaDetailsViewModel.dwhEndTime.getMinutes()
      );

      if (startTimeInputCompare! >= endTimeInputCompare!) {
        setIsStartTimeAfterEndTimeError(true);
        setCanFormBeSubmitted(false);
        return;
      }

      const datesChanged = startTimeInputCompare !== startTimeCurrentCompare || endTimeInputCompare !== endTimeCurrentCompare;

      setCanFormBeSubmitted(datesChanged);
    } else {
      setCanFormBeSubmitted(false);
    }
  };

  useEffect(() => {
    if (slaDetailsViewModel) {
      canFormBeSubmittedCheck();
    }
  }, [slaDetailsViewModel]);

  useEffect(() => {
    if (dateHasChanged) {
      canFormBeSubmittedCheck();
      setDateHasChanged(false);
    }
  }, [dateHasChanged]);

  const handleUpdateDriverWorkingHoursClick = () => {
    const request : IUpdateDriverWorkingHoursRequestModel = {
      startTime: convertUtcDateToLocalDate(new Date(`1970-01-01T${format(startTimeRef!.value, "HH:mm")}`)).toISOString(),
      endTime: convertUtcDateToLocalDate(new Date(`1970-01-01T${format(endTimeRef!.value, "HH:mm")}`)).toISOString()
    };
    updateDriveWorkingHoursSendRequest("settings/sla/dwh", "PUT", request);
  };

  useEffect(() => {
    if (updateDriveWorkingHoursStatusCode === 200) {
      navigate(RoutePathsEnum.SettingsSla);
    }
  }, [updateDriveWorkingHoursStatusCode]);

  useEffect(() => {
    if (apiErrors) {
      setShowExceptions(true);
    }
  }, [apiErrors]);

  const exceptionAlertCloseHandler = useCallback(() => {
    setShowExceptions(false);
  }, [setShowExceptions]);

  return (
    <Page>
      {showExceptions && apiErrors && (
        <AmplifyError
          errors={apiErrors}
          isDismissable
          onCloseHandler={exceptionAlertCloseHandler}
        />
      )}
      <Card>
        <Card.Header>
          <div className="float-start">
            <h2 data-testid="manage-sla">
              Edit Driver Working Hours
              <Spinner
                animation="grow"
                visible={isSending}
                spinnerText="Loading"
                variant="info"
              />

            </h2>
          </div>
        </Card.Header>
        <Card.Body className="no-padding">
          {slaDetailsViewModel && startTimeInput && endTimeInput
          && (
            <Form autoComplete="off" onSubmit={handleSubmit(handleUpdateDriverWorkingHoursClick)}>
              <Row className="d-flex w-100 align-items-center top-margin-small bottom-margin-small">
                <Col xs={6} className="d-none d-md-block">
                  <Breadcrumb className="left-margin-large no-wrap">
                    <Breadcrumb.Item
                      data-testid="breadcrumb-settings"
                      linkAs={Link}
                      linkProps={{ to: RoutePathsEnum.Settings }}
                      onClick={() => navigate(RoutePathsEnum.Settings)}
                    >
                      Settings
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      data-testid="breadcrumb-sla-management"
                      linkAs={Link}
                      linkProps={{ to: RoutePathsEnum.SettingsSla }}
                      onClick={() => navigate(RoutePathsEnum.SettingsSla)}
                    >
                      SLA Management
                    </Breadcrumb.Item>
                    <Breadcrumb.Item
                      data-testid="breadcrumb-edit-driver-working-hours"
                      active
                    >
                      Edit Driver Working Hours
                    </Breadcrumb.Item>

                  </Breadcrumb>
                </Col>
                <Col className="text-end">
                  <Button
                    id="btnUpdate"
                    disabled={!canFormBeSubmitted}
                    variant="primary"
                    className="right-margin-small"
                    data-testid="btnUpdate"
                    aria-label="Update SLA"
                    type="submit"
                  >
                    Update
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate(RoutePathsEnum.SettingsSla)}
                    data-testid="btnCancel"
                    aria-label="Cancel and back to Settings"
                  >
                    Cancel
                  </Button>
                </Col>
              </Row>
              <Divider />
              <section className="edit-driver-working-hours pt-2">
                <Container fluid>
                  <Row xs={12} md={8}>
                    <div className="w-auto">
                      <Form.Group as={Row} className="w-50">
                        <Form.Label
                          id="startTimeLabel"
                          data-testid="startTimeLabel"
                          htmlFor="startTimeInput"
                          className="horiz-form-label"
                        >
                          Start Time:

                        </Form.Label>
                        <TimePickerComponent
                          ref={(t: TimePickerComponent) => {
                            startTimeRef = t;
                          }}
                          id="startTimeInput"
                          data-testid="startTime"
                          type="text"
                          htmlAttributes={{ "aria-labelledby": "startTimeLabel" }}
                          format="HH:mm"
                          value={startTimeInput}
                          onChange={() => { setDateHasChanged(true); }}
                          showClearButton={false}
                        />
                        {isStartTimeAfterEndTimeError && (
                          <span
                            data-testid="startTimeAfterEndTime"
                            className="validation-error"
                          >
                            Must be before the end time
                          </span>
                        )}
                      </Form.Group>
                      <Form.Group as={Row} className="w-50 mt-2">
                        <Form.Label
                          id="endTimeLabel"
                          data-testid="endTimeLabel"
                          htmlFor="endTimeInput"
                          className="horiz-form-label"
                        >
                          End Time:

                        </Form.Label>
                        <TimePickerComponent
                          ref={(t: TimePickerComponent) => {
                            endTimeRef = t;
                          }}
                          id="endTimeInput"
                          data-testid="endTime"
                          type="text"
                          htmlAttributes={{ "aria-labelledby": "endTimeLabel" }}
                          format="HH:mm"
                          value={endTimeInput}
                          onChange={() => { setDateHasChanged(true); }}
                          showClearButton={false}
                        />

                      </Form.Group>
                    </div>
                  </Row>
                  {slaDetailsViewModel.dwhLastModifiedBy && slaDetailsViewModel.dwhLastModifiedTimestamp && (
                    <Row className="mt-2">
                      <Col>
                        <p data-testid="lastModifiedByText">
                          {`Last Modified by: ${slaDetailsViewModel.dwhLastModifiedBy} on 
                      ${format(slaDetailsViewModel.dwhLastModifiedTimestamp!, "dd/MM/yyyy HH:mm:ss")}`}
                        </p>
                      </Col>
                    </Row>

                  )}
                </Container>
              </section>

            </Form>
          )}
        </Card.Body>
      </Card>
      <Button
        data-testid="btnTestSetStartDate"
        className="d-none"
        onClick={() => {
          const testStartDate = new Date(startTimeInput!.toISOString());
          testStartDate.setHours(0);
          setStartTimeInput(testStartDate);
          setDateHasChanged(true);
        }}
      >
        testing change event

      </Button>
    </Page>
  );
};

export default EditDriverWorkingHours;
